import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { changePassword, updateProfile } from '../../../../redux/actions/SocietyController/CtrlProfile/CtrlProfileAction';
import { CircularProgress } from '@mui/material';
import { notify } from '../../../../utils/notification';
import { useNavigate } from 'react-router-dom';
import { SOCIETY_PORTAL, DASHBOARD } from '../../../../Routing/routes';
import { selectedSocietyStorer } from '../../../../utils/webStorer';

const ViewProfileLayer = () => {
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const { loginData } = useSelector(state => state.loginReducer);
    const { loading, passwordChangedAt } = useSelector(state => state.CtrlProfileReducers);

    const [activeTab, setActiveTab] = useState('profile');
    const [password, setPassword] = useState({ currentPass: '', newPass: '' });
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleChangePassword = () => {
        if (!password?.currentPass || !password?.newPass) {
            notify('error', 'Please enter both your current and new password');
            return;
        }
        dispatch(changePassword({
            currentPassword: password?.currentPass,
            newPassword: password?.newPass
        }));
    };

    // The saga bumps passwordChangedAt only on a successful change. Without
    // this the inputs kept holding the old credentials after saving, so the
    // form still showed the previous password.
    useEffect(() => {
        if (!passwordChangedAt) return;
        setPassword({ currentPass: '', newPass: '' });
        setPasswordVisible(false);
        setConfirmPasswordVisible(false);
    }, [passwordChangedAt]);

    const user = loginData?.user || {};
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');

    // /auth/me carries the role on each society membership (societies[].role),
    // not on the user object, so user.role was always undefined and Role showed
    // "-". Show the role of the membership the user is currently working in.
    const selectedSociety = selectedSocietyStorer.getSociety();
    const memberships = loginData?.societies || [];
    const activeMembership =
        memberships.find(s => selectedSociety?.user_society_id != null && s.user_society_id === selectedSociety.user_society_id)
        || memberships.find(s => selectedSociety?.society_id != null && s.society_id === selectedSociety.society_id)
        || selectedSociety;
    const roleName = activeMembership?.role?.role_name || activeMembership?.role_name || user.role || '';

    // Profile edit form — held in local state so the inputs are actually
    // editable. Split first_name / last_name maps to the BE payload the
    // updateProfile request-builder expects (first_name, last_name, phone).
    // Email and role stay read-only (managed by admin, not the user).
    const [profileForm, setProfileForm] = useState({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
    });
    // Re-sync when loginData changes (e.g. after a successful save the redux
    // store updates and we want the inputs to reflect the persisted values).
    useEffect(() => {
        setProfileForm({
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            phone: user.phone || '',
        });
    }, [user.first_name, user.last_name, user.phone]);

    const handleProfileChange = (key) => (e) => {
        setProfileForm(prev => ({ ...prev, [key]: e.target.value }));
    };
    const handleProfileCancel = () => {
        setProfileForm({
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            phone: user.phone || '',
        });
        if (window.history.state?.idx > 0) navigate(-1);
        else navigate(SOCIETY_PORTAL + DASHBOARD);
    };
    const handleProfileSave = () => {
        dispatch(updateProfile({
            first_name: profileForm.first_name?.trim(),
            last_name: profileForm.last_name?.trim(),
            phone: profileForm.phone?.trim(),
        }));
    };

    return (
        <div className="row gy-4">
            {/* Left Sidebar */}
            <div className="col-lg-4">
                <div className="tw-rounded-xl tw-bg-white tw-border tw-border-slate-200 tw-shadow-sm tw-p-5">
                    {/* Avatar + Name */}
                    <div className="tw-text-center tw-pb-4 tw-border-b tw-border-slate-100">
                        <div className="tw-w-20 tw-h-20 tw-rounded-full tw-bg-amber-50 tw-border-2 tw-border-amber-200 tw-flex tw-items-center tw-justify-center tw-mx-auto">
                            <Icon icon="solar:user-circle-bold-duotone" className="tw-text-amber-400" style={{ fontSize: '48px' }} />
                        </div>
                        <h3 className="tw-text-base tw-font-bold tw-text-slate-800 tw-mt-3">{fullName || 'User'}</h3>
                        <p className="tw-text-xs tw-text-slate-500 tw-mt-0.5">{user.email || '-'}</p>
                        {roleName && (
                            <span className="tw-inline-block tw-mt-2 tw-px-3 tw-py-1 tw-rounded-full tw-bg-amber-50 tw-text-amber-600 tw-text-[11px] tw-font-semibold tw-border tw-border-amber-100">
                                {roleName}
                            </span>
                        )}
                    </div>

                    {/* Info List */}
                    <div className="tw-mt-4 tw-space-y-3">
                        {[
                            { label: 'Full Name', value: fullName, icon: 'solar:user-rounded-bold-duotone' },
                            { label: 'Email', value: user.email, icon: 'solar:letter-bold-duotone' },
                            { label: 'Phone', value: user.phone, icon: 'solar:phone-bold-duotone' },
                            { label: 'Role', value: roleName, icon: 'solar:shield-user-bold-duotone' },
                            { label: 'Joined', value: user.created_at ? moment(user.created_at).format('DD MMM YYYY') : '-', icon: 'solar:calendar-bold-duotone' },
                        ].map((item, idx) => (
                            <div key={idx} className="tw-flex tw-items-center tw-gap-3">
                                <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-amber-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                    <Icon icon={item.icon} className="tw-text-base tw-text-amber-500" />
                                </div>
                                <div className="tw-min-w-0 tw-flex-1">
                                    <p className="tw-text-[10px] tw-text-slate-400 tw-uppercase tw-tracking-wider tw-font-medium tw-leading-none tw-mb-1">{item.label}</p>
                                    <p className="tw-text-sm tw-text-slate-700 tw-font-medium tw-truncate">{item.value || '-'}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right Content */}
            <div className="col-lg-8">
                <div className="tw-rounded-xl tw-bg-white tw-border tw-border-slate-200 tw-shadow-sm">
                    {/* Tabs */}
                    <div className="tw-flex tw-gap-1 tw-p-1.5 tw-mx-5 tw-mt-4 tw-bg-slate-100 tw-rounded-lg tw-w-fit">
                        <button
                            type="button"
                            onClick={() => setActiveTab('profile')}
                            className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-rounded-md tw-transition-all ${
                                activeTab === 'profile'
                                    ? 'tw-bg-white tw-text-amber-600 tw-shadow-sm'
                                    : 'tw-text-slate-500 hover:tw-text-slate-700'
                            }`}
                        >
                            <Icon icon="solar:user-rounded-bold" className="tw-text-sm" />
                            Edit Profile
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('password')}
                            className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-rounded-md tw-transition-all ${
                                activeTab === 'password'
                                    ? 'tw-bg-white tw-text-amber-600 tw-shadow-sm'
                                    : 'tw-text-slate-500 hover:tw-text-slate-700'
                            }`}
                        >
                            <Icon icon="solar:lock-password-bold" className="tw-text-sm" />
                            Change Password
                        </button>
                    </div>

                    {/* Content */}
                    <div className="tw-p-5">
                        {activeTab === 'profile' && (
                            <div>
                                <div className="row">
                                    <div className="col-md-6 tw-mb-4">
                                        <label className="tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1.5 tw-block">First Name</label>
                                        <input
                                            type="text"
                                            className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-200 tw-rounded-lg focus:tw-outline-none focus:tw-border-amber-400 focus:tw-ring-1 focus:tw-ring-amber-400 tw-transition-colors"
                                            value={profileForm.first_name}
                                            onChange={handleProfileChange('first_name')}
                                            placeholder="Enter first name"
                                        />
                                    </div>
                                    <div className="col-md-6 tw-mb-4">
                                        <label className="tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1.5 tw-block">Last Name</label>
                                        <input
                                            type="text"
                                            className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-200 tw-rounded-lg focus:tw-outline-none focus:tw-border-amber-400 focus:tw-ring-1 focus:tw-ring-amber-400 tw-transition-colors"
                                            value={profileForm.last_name}
                                            onChange={handleProfileChange('last_name')}
                                            placeholder="Enter last name"
                                        />
                                    </div>
                                    <div className="col-md-6 tw-mb-4">
                                        <label className="tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1.5 tw-block">Email</label>
                                        <input
                                            type="email"
                                            className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-200 tw-rounded-lg tw-bg-slate-50 tw-text-slate-500 tw-cursor-not-allowed"
                                            value={user.email || ''}
                                            disabled
                                            title="Email is managed by admin"
                                        />
                                    </div>
                                    <div className="col-md-6 tw-mb-4">
                                        <label className="tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1.5 tw-block">Phone</label>
                                        <input
                                            type="text"
                                            className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-200 tw-rounded-lg focus:tw-outline-none focus:tw-border-amber-400 focus:tw-ring-1 focus:tw-ring-amber-400 tw-transition-colors"
                                            value={profileForm.phone}
                                            onChange={handleProfileChange('phone')}
                                            placeholder="10-digit phone number"
                                            maxLength={15}
                                        />
                                    </div>
                                    <div className="col-md-6 tw-mb-4">
                                        <label className="tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1.5 tw-block">Role</label>
                                        <input
                                            type="text"
                                            className="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-slate-200 tw-rounded-lg tw-bg-slate-50 tw-text-slate-500 tw-cursor-not-allowed"
                                            value={roleName || '-'}
                                            disabled
                                            title="Role is managed by admin"
                                        />
                                    </div>
                                </div>

                                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-2">
                                    <button
                                        type="button"
                                        onClick={handleProfileCancel}
                                        disabled={loading}
                                        className={`tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-text-slate-600 tw-bg-slate-100 hover:tw-bg-slate-200 tw-transition-colors ${loading ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleProfileSave}
                                        disabled={loading}
                                        className="tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-text-white tw-bg-amber-500 hover:tw-bg-amber-600 tw-transition-colors tw-flex tw-items-center tw-gap-1.5 disabled:tw-opacity-50"
                                    >
                                        {loading ? <CircularProgress size={14} color="inherit" /> : <Icon icon="solar:check-circle-bold" className="tw-text-base" />}
                                        {loading ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {activeTab === 'password' && (
                            <div>
                                <div className="row">
                                    <div className="col-md-6 tw-mb-4">
                                        <label className="tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1.5 tw-block">
                                            Current Password <span className="tw-text-red-400">*</span>
                                        </label>
                                        <div className="tw-relative">
                                            <input
                                                type={passwordVisible ? 'text' : 'password'}
                                                className="tw-w-full tw-px-3 tw-py-2 tw-pr-10 tw-text-sm tw-border tw-border-slate-200 tw-rounded-lg focus:tw-outline-none focus:tw-border-amber-400 focus:tw-ring-1 focus:tw-ring-amber-400 tw-transition-colors"
                                                placeholder="Enter current password"
                                                name="current_password_change"
                                                autoComplete="new-password"
                                                onChange={(e) => setPassword(val => ({ ...val, currentPass: e.target.value }))}
                                                value={password?.currentPass || ''}
                                            />
                                            <button type="button" onClick={() => setPasswordVisible(!passwordVisible)} className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400 hover:tw-text-slate-600">
                                                <Icon icon={passwordVisible ? 'solar:eye-closed-linear' : 'solar:eye-linear'} className="tw-text-lg" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-md-6 tw-mb-4">
                                        <label className="tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1.5 tw-block">
                                            New Password <span className="tw-text-red-400">*</span>
                                        </label>
                                        <div className="tw-relative">
                                            <input
                                                type={confirmPasswordVisible ? 'text' : 'password'}
                                                className="tw-w-full tw-px-3 tw-py-2 tw-pr-10 tw-text-sm tw-border tw-border-slate-200 tw-rounded-lg focus:tw-outline-none focus:tw-border-amber-400 focus:tw-ring-1 focus:tw-ring-amber-400 tw-transition-colors"
                                                placeholder="Enter new password"
                                                name="new_password_change"
                                                autoComplete="new-password"
                                                onChange={(e) => setPassword(val => ({ ...val, newPass: e.target.value }))}
                                                value={password?.newPass || ''}
                                            />
                                            <button type="button" onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400 hover:tw-text-slate-600">
                                                <Icon icon={confirmPasswordVisible ? 'solar:eye-closed-linear' : 'solar:eye-linear'} className="tw-text-lg" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-2">
                                    <button
                                        type="button"
                                        className={`tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-text-slate-600 tw-bg-slate-100 hover:tw-bg-slate-200 tw-transition-colors ${loading ? 'tw-opacity-50' : ''}`}
                                        onClick={() => setPassword({ currentPass: '', newPass: '' })}
                                        disabled={loading}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        type="button"
                                        className="tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-text-white tw-bg-amber-500 hover:tw-bg-amber-600 tw-transition-colors tw-flex tw-items-center tw-gap-1.5 disabled:tw-opacity-50"
                                        onClick={handleChangePassword}
                                        disabled={loading}
                                    >
                                        {loading ? <CircularProgress size={14} color="inherit" /> : <Icon icon="solar:check-circle-bold" className="tw-text-base" />}
                                        {loading ? 'Saving...' : 'Update Password'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewProfileLayer;
